import React, { useContext } from 'react';
import { Modal } from 'react-bootstrap';
import { Button, GlobalContext, H4, P } from '@d19n/ui-core';
import styled from 'styled-components';

/**
 * WEB-1884 — the one place a server failure is shown to the customer.
 *
 * Six places in this site report a failure by calling `setAPIErrorMsg`, and until
 * now nothing read it: a failed postcode search or address lookup left the page
 * exactly as it was and the customer saw nothing happen. This is the residential
 * site's error modal, which already does the job there.
 *
 * It opens on there being a message, not on a separate flag, because none of the
 * callers set one.
 */

const CloseWrapper = styled.div`
  cursor: pointer;
  top: 0.5rem;
  right: 1rem;
  position: absolute;
  height: 1.5rem;
  width: 1.5rem;
  align-items: center;
  display: inline-flex;
  justify-content: center;
  z-index: 10;
  color: #000;
  @media (min-width: 768px) {
    right: 1rem;
    top: 1rem;
  }
`;

const CloseButton = ({ onClick }: { onClick: () => void }) => (
  <CloseWrapper onClick={onClick}>
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
      <path
        d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
        fill="currentColor"
      ></path>
    </svg>
  </CloseWrapper>
);

const ErrorModal = () => {
  const gContext = useContext(GlobalContext) as any;

  const message = gContext?.APIErrorMsg?.message;

  const handleClose = () => {
    gContext?.setErrorModalVisible?.(false);
    gContext?.setAPIErrorMsg?.({ title: '', message: '' });
  };

  /*
   * The customer is told the same plain thing whatever went wrong. The messages
   * these failures carry are written for us, not for them — one of them names
   * Odin — and they were never read by anybody until this modal existed.
   */
  const CUSTOMER_MESSAGE =
    'There was a problem accessing our servers. Please try again later.';

  return (
    <Modal
      show={Boolean(message) || Boolean(gContext?.errorModalVisible)}
      onHide={handleClose}
      size="lg"
      aria-labelledby="error-modal-title"
      centered
      role="dialog"
      style={{ borderRadius: '10px', paddingRight: 0 }}
    >
      <Modal.Body className="p-4 position-relative">
        <CloseButton onClick={handleClose} />
        <H4 id="error-modal-title" className="text-center mb-30 mt-20">
          {gContext?.APIErrorMsg?.title || 'Error'}
        </H4>
        <P className="text-center mb-30">{CUSTOMER_MESSAGE}</P>
        <div className="text-center">
          <Button
            className="medium-button blue-bg mb-3"
            text="Close"
            aria-label="Close"
            type="primary"
            onClick={handleClose}
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ErrorModal;
